import { put, select, call, all, takeLatest } from "redux-saga/effects";
import CompanyApi from "../api/Company";
import StorageApi from "../api/Storage";
import FileCategoriesApi from "../api/FileCategories";
import { populateFileCategories } from "../utils/populateFileCategories";
import { Auth, Company, FileCategories } from "../actions/";
// Type
import type { Action } from "deox";
import type { tAPI } from "../api/API";
//Sagas for company
function* doCompanyFetchCompany(
  action: Action<
    string,
    {
      companyId: string;
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Company.companyFetchCompanyRequest());
  window.logger.log("doCompanyFetchCompany", action);
  const { companyId } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to get my company
    const { data } = yield call(CompanyApi.GetCompany, api, companyId, {
      token,
    });
    const { company } = data;
    yield put(Company.companyFetchCompanySuccess({ myCompany: company }));
  } catch (error) {
    const { data: errors } = error;
    window.logger.error("ErrorSaga - doCompanyFetchCompany", error, error);
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Company.companyFetchCompanyFailure());
  }
}

function* doCompanyEditCompany(
  action: Action<
    string,
    {
      companyId: string;
      body: object;
      isPostFile?: boolean;
      callbackSuccess?: () => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Company.companyEditCompanyRequest());
  window.logger.log("doCompanyEditCompany", action);
  const {
    companyId,
    body,
    isPostFile: isUpload = false,
    callbackSuccess = null,
    callbackError = null,
  } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    let patchBody = body;
    // If uploading a logo, do S3 upload first, then PATCH with fileId
    if (isUpload) {
      const { fileId } = yield call(
        StorageApi.UploadFile,
        api,
        body as unknown as File,
        {
          belongsTo: "Companies" as const,
          ownerId: companyId,
          location: "profile" as const,
        },
        { token }
      );
      patchBody = { logo: fileId };
    }
    // call the api to update the company profile
    const { data } = yield call(
      CompanyApi.EditCompany,
      api,
      companyId,
      patchBody,
      false,
      { token }
    );
    const { company } = data;
    yield put(Company.companyEditCompanySuccess({ myCompany: company }));
    if (callbackSuccess) {
      callbackSuccess();
    }
  } catch (error) {
    const { data: errors } = error;
    window.logger.error("ErrorSaga - doCompanyEditCompany", error, errors);
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Company.companyEditCompanyFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

function* doCompanyFetchCompanyFiles(
  action: Action<
    string,
    {
      id: string;
      callbackSuccess?: (data: any, extradata: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Company.companyFetchCompanyFilesRequest());
  window.logger.log("doCompanyFetchCompanyFiles", action);
  const { id, callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to get all files from this company from specific id
    const { data: dataFileCategories } = yield call(
      FileCategoriesApi.GetFileCategories,
      api,
      { token }
    );
    const { categories: fileCategories } = dataFileCategories;
    const { data } = yield call(CompanyApi.GetCompanyFiles, api, id, { token });
    const { files } = data;
    const populatedFiles = populateFileCategories(files, fileCategories);
    yield put(
      Company.companyFetchCompanyFilesSuccess({ files: populatedFiles })
    );
    if (callbackSuccess) {
      callbackSuccess(populatedFiles, fileCategories);
    }
  } catch (error) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doCompanyFetchCompanyFiles",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Company.companyFetchCompanyFilesFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

function* doCompanyFetchCompanyFilesExpired(
  action: Action<
    string,
    {
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Company.companyFetchCompanyFilesExpiredRequest());
  window.logger.log("doCompanyFetchCompanyFilesExpired", action);
  const { callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    const { data } = yield call(CompanyApi.GetCompanyFilesExpired, api, {
      token,
    });
    const { files: filesExpired } = data;
    // Populate category: v3 returns category as string ID
    let fc: any[] = yield select(
      ({ fileCategories: { fileCategories } }) => fileCategories
    );
    if (!fc || fc.length === 0) {
      const { data: fcData } = yield call(
        FileCategoriesApi.GetFileCategories,
        api,
        { token }
      );
      fc = fcData.categories;
      yield put(
        FileCategories.fileCategoriesFetchFileCategoriesSuccess({
          fileCategories: fc,
        })
      );
    }
    const populatedFilesExpired = populateFileCategories(filesExpired, fc);
    yield put(
      Company.companyFetchCompanyFilesExpiredSuccess({
        filesExpired: populatedFilesExpired,
      })
    );
    if (callbackSuccess) {
      callbackSuccess(populatedFilesExpired);
    }
  } catch (error) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doCompanyFetchCompanyFilesExpired",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Company.companyFetchCompanyFilesExpiredFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

function* doCompanyUploadCompanyFiles(
  action: Action<
    string,
    {
      body: {
        file: File;
        belongsTo: string;
        ownerId: string;
        location: string;
        metadata: { [k: string]: any };
      };
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any) => void;
    }
  >
) {
  yield put(Company.companyUploadCompanyFilesRequest());
  window.logger.log("doCompanyUploadCompanyFiles", action);
  const { body, callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // Upload file to S3 via 3-step flow
    const { fileId, file: confirmedFile } = yield call(
      StorageApi.UploadFile,
      api,
      body.file,
      {
        belongsTo: body.belongsTo as any,
        ownerId: body.ownerId,
        location: body.location as any,
      },
      { token }
    );
    // Set file metadata if provided
    if (body.metadata) {
      yield call(StorageApi.EditFile, api, fileId, body.metadata, { token });
    }
    yield put(Company.companyUploadCompanyFilesSuccess());
    if (callbackSuccess) {
      callbackSuccess([confirmedFile]);
    }
  } catch (error) {
    const { data: errors } = error;
    window.logger.error(
      "ErrorSaga - doCompanyUploadCompanyFiles",
      error,
      errors
    );
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(Company.companyUploadCompanyFilesFailure());
    const { message } = errors || "";
    if (callbackError) {
      callbackError(message);
    }
  }
}

export default function* companySagas(): Generator<any, any, any> {
  yield all([
    takeLatest(Company.COMPANY_FETCH_COMPANY, doCompanyFetchCompany),
    takeLatest(Company.COMPANY_EDIT_COMPANY, doCompanyEditCompany),
    takeLatest(Company.COMPANY_FETCH_COMPANY_FILES, doCompanyFetchCompanyFiles),
    takeLatest(
      Company.COMPANY_FETCH_COMPANY_FILES_EXPIRED,
      doCompanyFetchCompanyFilesExpired
    ),
    takeLatest(
      Company.COMPANY_UPLOAD_COMPANY_FILES,
      doCompanyUploadCompanyFiles
    ),
  ]);
}
